import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { deleteOrder, getOrders } from '../services/orders';
import TrashButton from '../components/common/TrashButton';
import Pagination from '../components/common/Pagination';
import './OrdersPage.css';

const ITEMS_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 350;
const STATUS_FILTERS = [
  { key: 'all', label: 'Todos', value: null },
  { key: 'open', label: 'Abiertos', value: false },
  { key: 'closed', label: 'Cerrados', value: true },
];

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('es-ES') : '—');

const formatHours = (hours) => (hours > 0 ? `${hours.toFixed(2).replace('.', ',')} h` : '—');

// El 71% de los importes están a cero porque la facturación vive en su ERP:
// pintar "0 €" haría creer que el trabajo se hizo gratis.
const formatAmount = (amount) => (
  amount > 0 ? amount.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' }) : '—'
);

const OrdersPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Página, búsqueda y filtro viven en la URL: así volver desde un pedido te deja
  // donde estabas, el botón atrás del navegador funciona y el enlace se puede guardar.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get('page'), 10) || 1);
  const search = searchParams.get('search') || '';
  const statusFilter = searchParams.get('status') || 'all';

  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const updateParams = (changes, replace = false) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) => {
      if (value === null || value === '' || value === 'all' || value === 1) next.delete(key);
      else next.set(key, String(value));
    });
    setSearchParams(next, { replace });
  };

  const loadOrders = useCallback(async (targetPage, searchTerm, status) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const closed = STATUS_FILTERS.find((f) => f.key === status)?.value;
      const data = await getOrders({
        skip: (targetPage - 1) * ITEMS_PER_PAGE,
        limit: ITEMS_PER_PAGE,
        search: searchTerm,
        closed,
      });
      // Descarta respuestas de búsquedas que el usuario ya ha reemplazado
      if (requestId !== requestIdRef.current) return;
      setOrders(data.orders || []);
      setTotal(data.total || 0);
      setError(null);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setOrders([]);
      setTotal(0);
      setError(err.message || 'No se han podido cargar los pedidos');
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => loadOrders(page, search, statusFilter), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [page, search, statusFilter, loadOrders]);

  // replace al escribir: si no, cada letra deja una entrada en el historial del navegador
  const handleSearch = (value) => updateParams({ search: value, page: 1 }, true);

  const handleStatusFilter = (key) => updateParams({ status: key, page: 1 });

  // LAKPED-18: un pedido es "borrador" mientras nadie ha trabajado en él: abierto, sin partes y sin
  // albarán. El servidor vuelve a comprobarlo antes de borrar.
  const esBorrador = (order) => !order.closed && !order.parts_count && !order.delivery_note;

  const borrar = async (order) => {
    if (!window.confirm(`¿Borrar el pedido de ${order.client?.name || 'sin cliente'}?`)) return;
    try {
      await deleteOrder(order.hash);
      loadOrders(page, search, statusFilter);
    } catch (err) {
      alert(err.response?.data?.detail || 'No se ha podido borrar el pedido');
    }
  };

  const handleOpenOrder = (orderHash) => {
    navigate(`/orders/${orderHash}`, { state: { from: location.search } });
  };

  const totalPages = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));

  return (
    <div className="orders-container">
      <div className="orders-head">
        <h1 className="orders-title">Pedidos</h1>
        <button className="orders-new" onClick={() => navigate('/orders/new')}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Nuevo pedido
        </button>
      </div>

      <div className="orders-toolbar">
        <div className="orders-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            type="text"
            placeholder="Buscar por cliente, referencia o descripción"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {search && (
            <button className="orders-search-clear" onClick={() => handleSearch('')} title="Limpiar">×</button>
          )}
        </div>

        <div className="orders-status-filter">
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter.key}
              className={`orders-status-btn ${statusFilter === filter.key ? 'active' : ''}`}
              onClick={() => handleStatusFilter(filter.key)}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="orders-error">{error}</div>}

      <div className="orders-table-wrapper">
        <table className="orders-table">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Fecha</th>
              <th>Referencia</th>
              <th>Trabajo</th>
              <th className="col-num">Horas</th>
              <th className="col-num">Importe</th>
              <th className="col-estado">Estado</th>
              <th className="col-papelera" />
            </tr>
          </thead>
          <tbody>
            {loading && orders.length === 0 && (
              <tr><td colSpan="8" className="orders-empty">Cargando…</td></tr>
            )}
            {!loading && orders.length === 0 && (
              <tr><td colSpan="8" className="orders-empty">
                {search ? `Sin pedidos para “${search}”` : 'Todavía no hay pedidos'}
              </td></tr>
            )}
            {orders.map((order) => (
              <tr key={order.hash} onClick={() => handleOpenOrder(order.hash)} className="orders-row">
                <td className="col-cliente">{order.client?.name || '—'}</td>
                <td>{formatDate(order.order_date)}</td>
                <td className="col-ref">{order.client_reference || '—'}</td>
                <td className="col-trabajo">
                  <div className="orders-desc">{order.description || '—'}</div>
                  {order.analysis && (
                    <span className="orders-plano" title={order.analysis.filename}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <path d="M14 2v6h6" />
                      </svg>
                      {order.analysis.filename}
                    </span>
                  )}
                </td>
                <td className="col-num">{formatHours(order.total_hours)}</td>
                <td className="col-num">{formatAmount(order.invoiced_amount)}</td>
                <td className="col-estado">
                  <span className={`orders-badge ${order.closed ? 'cerrado' : 'abierto'}`}>
                    {order.closed ? 'Cerrado' : 'Abierto'}
                  </span>
                </td>
                <td className="col-papelera">
                  {esBorrador(order) && <TrashButton title="Borrar el pedido" onClick={() => borrar(order)} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        totalItems={total}
        itemLabel={total === 1 ? 'pedido' : 'pedidos'}
        loading={loading}
        onChange={(target) => updateParams({ page: target })}
      />

    </div>
  );
};

export default OrdersPage;
